/** A return target must remain internal after browser URL normalization.
 * Check decoded representations too; return the original URL's normalized
 * path, never a decoded query (which could change invitation tokens).
 */
function hasUnsafeCharacters(value: string): boolean {
  return value.includes('\\') || Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
  });
}

export function safeAuthReturn(value: unknown, origin = window.location.origin): string | undefined {
  if (typeof value !== 'string' || value.length > 4096) return undefined;
  try {
    const base = new URL(origin);
    if (hasUnsafeCharacters(value)) return undefined;
    // Search and hash are opaque to navigation. Decoding them here would
    // reinterpret legitimate token values such as `q=50%25`.
    let candidate = value.split(/[?#]/, 1)[0]!;
    for (;;) {
      if (!candidate.startsWith('/') || candidate.startsWith('//') || hasUnsafeCharacters(candidate)) return undefined;
      const parsed = new URL(candidate, base);
      if (parsed.origin !== base.origin || parsed.pathname.startsWith('//')) return undefined;
      const decoded = decodeURIComponent(candidate);
      if (decoded === candidate) break;
      candidate = decoded;
    }
    const parsed = new URL(value, base);
    if (parsed.origin !== base.origin || parsed.pathname.startsWith('//')) return undefined;
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return undefined;
  }
}

/** Transport the existing invitation proof during signup only when the safe
 * return names the canonical invitation page. The API still validates its
 * recipient, liveness and use; a referral token has a separate meaning. */
export function invitationTokenFromAuthReturn(value: unknown): string | undefined {
  const next = safeAuthReturn(value);
  if (!next) return undefined;
  const pathname = next.split(/[?#]/, 1)[0];
  return /^\/invites\/([A-Za-z0-9_-]{8,512})$/.exec(pathname ?? '')?.[1];
}

const RETURN_KEY = 'projoint:auth-return';

/** Session-only, account-bound continuation for a verification round trip. */
export function rememberAuthReturn(userId: string, value: unknown): void {
  const next = safeAuthReturn(value);
  if (!next) return;
  try { sessionStorage.setItem(RETURN_KEY, JSON.stringify({ userId, next, expiresAt: Date.now() + 86_400_000 })); } catch { /* storage may be disabled */ }
}

export function pendingAuthReturn(userId: string | undefined): string | undefined {
  if (!userId) return undefined;
  try {
    const item = JSON.parse(sessionStorage.getItem(RETURN_KEY) ?? 'null') as { userId?: unknown; next?: unknown; expiresAt?: number } | null;
    if (item?.userId === userId && typeof item.expiresAt === 'number' && item.expiresAt > Date.now()) return safeAuthReturn(item.next);
  } catch { /* malformed/disabled storage is not a return target */ }
  return undefined;
}

export function clearAuthReturn(): void {
  try { sessionStorage.removeItem(RETURN_KEY); } catch { /* nonessential */ }
}
